import type { BodyLandmarks } from "../../pose-detect";

/**
 * Shared MediaPipe BlazePose skeleton overlay. Rendered on top of the
 * user's photo during AI sizing flows (both the standalone snap → scan
 * path AND the in-wizard profile-creation calculating step).
 *
 * Previously the wizard had its own copy of this renderer which:
 *  - omitted the nose → shoulder bridge (head floated detached)
 *  - skipped the visibility check (drew offscreen joints as a top-left blob)
 *  - tuned dot/stroke widths relative to image dimensions (looked huge on
 *    portrait photos)
 *
 * Both consumers share this component so the visual is identical.
 */

// Joint pairs to draw as connecting lines. Nose ↔ shoulder bridges the
// head-torso gap so the skeleton reads as a connected body, not a head
// floating above the body.
const SKELETON_CONNECTIONS: Array<[keyof BodyLandmarks, keyof BodyLandmarks]> = [
	["nose", "leftShoulder"], ["nose", "rightShoulder"],
	["leftShoulder", "rightShoulder"],
	["leftShoulder", "leftElbow"], ["leftElbow", "leftWrist"],
	["rightShoulder", "rightElbow"], ["rightElbow", "rightWrist"],
	["leftShoulder", "leftHip"], ["rightShoulder", "rightHip"],
	["leftHip", "rightHip"],
	["leftHip", "leftKnee"], ["leftKnee", "leftAnkle"],
	["rightHip", "rightKnee"], ["rightKnee", "rightAnkle"],
];

// MediaPipe returns every joint regardless of whether it could see it —
// occluded joints come back at (0,0) or with low visibility. Without this
// filter the unseen joints stack into the image's top-left corner as a
// stray blob and the nose→shoulder line draws into the corner whenever
// the face is partially out of frame.
function isVisible(p: unknown, minVisibility: number): p is { x: number; y: number; visibility?: number } {
	if (!p || typeof p !== "object") return false;
	const pt = p as { x?: unknown; y?: unknown; visibility?: unknown };
	if (typeof pt.x !== "number" || typeof pt.y !== "number") return false;
	if (typeof pt.visibility === "number" && pt.visibility < minVisibility) return false;
	if (pt.x < 0.001 && pt.y < 0.001) return false;
	return true;
}

export interface PoseOverlayProps {
	landmarks: BodyLandmarks;
	/** Image width in pixels — used for the SVG viewBox so the overlay
	 *  scales with the underlying photo via `preserveAspectRatio="xMidYMid meet"`. */
	width: number;
	height: number;
	/** Optional `className`/`style` to position the SVG. Defaults to
	 *  absolutely-filling the parent container, which is what both
	 *  consumers want. */
	className?: string;
	style?: React.CSSProperties;
	minVisibility?: number;
}

export function PoseOverlay({ landmarks, width, height, className, style, minVisibility = 0.3 }: PoseOverlayProps) {
	// Stroke + dot sizing is responsive to the image's natural dimensions
	// (NOT a fixed pixel value) so the overlay looks proportionally smooth
	// on both small phone-camera thumbnails and large studio portraits.
	// The constants below were tuned so a 687×1024 photo renders ~4 px
	// strokes and ~5 px dots on screen — small, clean, no visual "blob"
	// over the body. NO halo, NO pulse animation: a single solid circle
	// per joint keeps the overlay smooth and reads as a tracing layer
	// rather than competing with the photo for attention.
	const longEdge = Math.max(width, height);
	const stroke = Math.max(2, longEdge / 260);
	const dotR = Math.max(3, longEdge / 200);

	return (
		<svg
			className={className}
			viewBox={`0 0 ${width} ${height}`}
			preserveAspectRatio="xMidYMid meet"
			style={style ?? { position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
			aria-hidden="true"
		>
					{SKELETON_CONNECTIONS.map(([a, b], i) => {
						const pa = landmarks[a]; const pb = landmarks[b];
						if (!isVisible(pa, minVisibility) || !isVisible(pb, minVisibility)) return null;
					return (
						<line
							key={`l-${i}`}
							x1={pa.x * width} y1={pa.y * height}
							x2={pb.x * width} y2={pb.y * height}
							stroke="rgba(100,210,255,0.9)" strokeWidth={stroke} strokeLinecap="round"
							opacity="0"
							style={{ animation: `ps-pose-fade 0.4s ease ${i * 0.05}s forwards` }}
						/>
					);
					})}
					{Object.entries(landmarks)
						.filter(([, v]) => isVisible(v, minVisibility))
					.map(([key, v], i) => {
						const p = v as { x: number; y: number };
						return (
							<circle
								key={key}
								cx={p.x * width} cy={p.y * height} r={dotR}
								fill="rgba(100,210,255,0.95)"
								opacity="0"
								style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }}
							/>
						);
					})}
		</svg>
	);
}
